import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, TrendingUp, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Tools & Calculators", href: "/tools" },
  { label: "Contact", href: "/contact" },
];

export default function Layout() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Navbar */}
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 h-16 transition-colors duration-300 ${
          scrolled
            ? "bg-slate-950/95 backdrop-blur-md border-b border-slate-800/70 shadow-lg shadow-black/20"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <TrendingUp className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="font-outfit text-lg font-700 text-white tracking-tight">
              Kara Finance{" "}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.href
                    ? "text-emerald-400 bg-emerald-500/10"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/budget-pro"
              data-testid="nav-budget-pro-button"
              className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 hover:text-white font-medium text-sm rounded-full px-4 py-2 transition-colors duration-200"
            >
              Budget Pro
            </Link>
            <a
              href="https://www.krishkara.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-button"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm rounded-full px-5 py-2 transition-all duration-200 hover:shadow-[0_0_16px_rgba(16,185,129,0.3)] hover:-translate-y-0.5"
            >
              Get the Guide
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-72 bg-slate-950 border-slate-800 p-0"
            >
              <div className="flex flex-col h-full">
                {/* Mobile Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-800">
                  <Link to="/" className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <TrendingUp className="w-3.5 h-3.5 text-slate-950" strokeWidth={2.5} />
                    </div>
                    <span className="font-outfit font-600 text-white">Kara Finance</span>
                  </Link>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Mobile Links */}
                <div className="flex flex-col gap-1 p-4 flex-1">
                  {NAV_LINKS.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                        location.pathname === link.href
                          ? "text-emerald-400 bg-emerald-500/10 border border-emerald-500/20"
                          : "text-slate-300 hover:text-white hover:bg-slate-800"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>

                {/* Mobile CTA */}
                <div className="p-4 border-t border-slate-800 space-y-3">
                  <Link
                    to="/budget-pro"
                    data-testid="mobile-budget-pro-button"
                    className="flex items-center justify-center w-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 hover:text-white font-medium text-sm rounded-full px-5 py-3 transition-colors duration-200"
                  >
                    Budget Pro
                  </Link>
                  <a
                    href="https://www.krishkara.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-cta-button"
                    className="flex items-center justify-center w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm rounded-full px-5 py-3 transition-all duration-200"
                  >
                    Get the Guide
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Page Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer data-testid="footer" className="bg-slate-950 border-t border-slate-800/60 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
                </div>
                <span className="font-outfit text-lg font-semibold text-white">Kara Finance</span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed max-w-xs">
                The ultimate toolkit for UK investors. Calculate, project, and track your financial freedom journey.
              </p>
            </div>

            {/* Links */}
            <div className="space-y-4">
              <h4 className="font-outfit font-semibold text-slate-200 text-sm uppercase tracking-widest">Tools</h4>
              <ul className="space-y-2">
                {[
                  { label: "Compound Interest", href: "/tools/compound-interest" },
                  { label: "UK Take Home Pay", href: "/tools/salary-calculator" },
                  { label: "ISA vs GIA", href: "/tools/isa-calculator" },
                  { label: "Emergency Fund", href: "/tools/emergency-fund" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="text-slate-500 hover:text-emerald-400 text-sm transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <div className="space-y-4">
              <h4 className="font-outfit font-semibold text-slate-200 text-sm uppercase tracking-widest">Newsletter</h4>
              <p className="text-slate-500 text-sm">
                Join thousands of UK investors getting weekly insights.
              </p>
              <a
                href="https://www.krishkara.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-newsletter-button"
                className="inline-flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-medium text-sm rounded-full px-5 py-2.5 transition-all duration-200 hover:border-emerald-400/50"
              >
                Join the Newsletter
              </a>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-10 pt-6 border-t border-slate-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-600 text-xs">
              &copy; {new Date().getFullYear()} Kara Finance UK. All rights reserved.
            </p>
            <p className="text-slate-700 text-xs">
              Not financial advice. For informational purposes only.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
